import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useSettings } from "../contexts/SettingsContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ShoppingCart, BookOpen, LogOut, Settings2 } from "lucide-react";

export const SPACE_KEY = "app_space";
export const setSpace = (s) => sessionStorage.setItem(SPACE_KEY, s);
export const getSpace = () => sessionStorage.getItem(SPACE_KEY);

const HomePage = () => {
  const navigate = useNavigate();
  const { user, logout, hasModule, updateUser } = useAuth();
  const { settings, logoUrl } = useSettings();
  const [params] = useSearchParams();
  const choose = (space, to) => { setSpace(space); navigate(to); };
  const spaces = user?.permissions?.spaces || ["commercial", "compta"];
  const canCompta = hasModule("accounts") && spaces.includes("compta");
  const canCommercial = spaces.includes("commercial");
  const commercialTo = user?.role === "caissier" ? "/cash" : "/dashboard";
  const autoDone = useRef(false);

  useEffect(() => {
    if (autoDone.current || params.get("choose") || !user) return;
    autoDone.current = true;
    const pref = user.default_space || "ask";
    if (pref === "commercial" && canCommercial) choose("commercial", commercialTo);
    else if (pref === "compta" && canCompta) choose("compta", "/accounting");
    else if (canCommercial && !canCompta) choose("commercial", commercialTo);
    else if (canCompta && !canCommercial) choose("compta", "/accounting");
    // eslint-disable-next-line
  }, [user]);

  const savePref = async (v) => {
    try { const r = await api.put("/auth/me/preferences", { default_space: v }); updateUser(r.data); toast.success(v === "ask" ? "L'accueil vous demandera l'espace à chaque connexion" : `L'espace ${v === "compta" ? "Comptabilité" : "Gestion commerciale"} s'ouvrira directement à votre connexion`); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white flex flex-col" data-testid="home-launcher">
      <div className="flex items-center justify-between px-6 lg:px-10 py-5 border-b border-white/10">
        <div className="flex items-center gap-3">
          <img src={logoUrl} alt={settings.name} className="w-11 h-11 rounded-sm object-contain bg-white/90 p-0.5" />
          <div><p className="font-[Chivo] font-bold text-lg leading-tight">{settings.name}</p>{settings.slogan && <p className="text-xs text-slate-400">{settings.slogan}</p>}</div>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <span className="text-slate-300">Bonjour, <span className="font-medium text-white">{user?.name}</span></span>
          <button onClick={logout} className="flex items-center gap-1 text-slate-400 hover:text-white transition-colors" data-testid="home-logout-btn"><LogOut className="w-4 h-4" /> Déconnexion</button>
        </div>
      </div>
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-10">
        <p className="overline text-slate-400 mb-2">Choisissez votre espace</p>
        <h1 className="text-2xl sm:text-3xl font-[Chivo] font-bold mb-10 text-center">Que souhaitez-vous faire aujourd'hui ?</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-3xl">
          <button onClick={() => canCommercial && choose("commercial", commercialTo)} disabled={!canCommercial} className={`group text-left rounded-sm p-8 border-4 border-transparent transition-all shadow-xl ${canCommercial ? "bg-white text-slate-900 hover:border-green-500 hover:-translate-y-1" : "bg-slate-800 text-slate-500 cursor-not-allowed"}`} data-testid="space-commercial-btn">
            <div className={`w-16 h-16 rounded-sm text-white flex items-center justify-center mb-5 group-hover:scale-105 transition-transform ${canCommercial ? "bg-green-600" : "bg-slate-700"}`}><ShoppingCart className="w-9 h-9" /></div>
            <p className="font-[Chivo] font-bold text-xl">1 · Gestion Commerciale</p>
            <p className="text-sm text-slate-500 mt-2">{canCommercial ? "Factures, achats, stock, clients et fournisseurs, caisse, banque, tableau de bord." : "Programme non autorisé pour votre compte."}</p>
          </button>
          <button onClick={() => canCompta && choose("compta", "/accounting")} disabled={!canCompta} className={`group text-left rounded-sm p-8 border-4 border-transparent transition-all shadow-xl ${canCompta ? "bg-white text-slate-900 hover:border-blue-500 hover:-translate-y-1" : "bg-slate-800 text-slate-500 cursor-not-allowed"}`} data-testid="space-compta-btn">
            <div className={`w-16 h-16 rounded-sm text-white flex items-center justify-center mb-5 group-hover:scale-105 transition-transform ${canCompta ? "bg-blue-700" : "bg-slate-700"}`}><BookOpen className="w-9 h-9" /></div>
            <p className="font-[Chivo] font-bold text-xl">2 · Comptabilité</p>
            <p className={`text-sm mt-2 ${canCompta ? "text-slate-500" : "text-slate-500"}`}>{canCompta ? "Journaux mensuels SYSCOHADA, comptes et lettrage, grand livre, balance, résultat, rapprochement, clôture." : "Programme non autorisé pour votre compte."}</p>
          </button>
        </div>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-3 text-sm text-slate-300 bg-white/5 border border-white/10 rounded-sm px-4 py-3" data-testid="default-space-pref">
          <Settings2 className="w-4 h-4 text-slate-400" />
          <span>À ma connexion, ouvrir directement :</span>
          <Select value={user?.default_space || "ask"} onValueChange={savePref}>
            <SelectTrigger className="h-8 w-56 rounded-sm bg-slate-800 border-slate-600 text-white" data-testid="default-space-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ask">Me demander (cet écran)</SelectItem>
              {canCommercial && <SelectItem value="commercial">Gestion Commerciale</SelectItem>}
              {canCompta && <SelectItem value="compta">Comptabilité</SelectItem>}
            </SelectContent>
          </Select>
        </div>
        <p className="text-xs text-slate-500 mt-6 text-center max-w-xl">Les deux espaces partagent les mêmes données : chaque facture, règlement et mouvement de caisse de la gestion commerciale apparaît automatiquement dans les journaux et les comptes de tiers de la comptabilité.</p>
      </div>
    </div>
  );
};

export default HomePage;
